(() => {
  /* ===== Product Carousel ===== */
  const initProductCarousel = (sectionEl) => {
    if (!sectionEl || sectionEl.dataset.pcInit === '1') return;
    sectionEl.dataset.pcInit = '1';

    const track = sectionEl.querySelector('.product-carousel__track');
    const prevBtn = sectionEl.querySelector('.product-carousel__arrow--prev');
    const nextBtn = sectionEl.querySelector('.product-carousel__arrow--next');

    if (!track || !prevBtn || !nextBtn) return;

    const getScrollAmount = () => {
      const item = track.querySelector('.product-carousel__item');
      if (!item) return 320;

      const rect = item.getBoundingClientRect();
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;

      return rect.width + gap;
    };

    const scrollByOne = (dir) => {
      track.scrollBy({ left: dir * getScrollAmount(), behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', () => scrollByOne(-1));
    nextBtn.addEventListener('click', () => scrollByOne(1));
  };

  const initAllProductCarousels = (scope = document) => {
    scope.querySelectorAll('.product-carousel').forEach(initProductCarousel);
  };

  /* ===== CR Video Reels ===== */
  const supportsIO = typeof IntersectionObserver !== 'undefined';

  const initCrReels = (root) => {
    if (!root || root.dataset.crInit === '1') return;
    root.dataset.crInit = '1';

    const track = root.querySelector('.cr-reels__track');
    const slides = Array.from(root.querySelectorAll('.cr-reels__slide'));
    if (!track || !slides.length) return;

    const centerScale = (parseFloat(root.dataset.centerScale || '112') / 100);
    const sideScale   = (parseFloat(root.dataset.sideScale || '94') / 100);

    const realSlides = slides.filter(s => (s.dataset.clone || 'false') === 'false');
    const n = realSlides.length;
    const cloneCount = Math.max(0, Math.floor((slides.length - n) / 2));
    const realStart = cloneCount;
    const realEnd   = cloneCount + n - 1;

    let stride = 0;
    const calcStride = () => {
      if (slides.length < 2) return 0;
      const a = slides[0].offsetLeft;
      const b = slides[1].offsetLeft;
      return Math.max(1, b - a);
    };

    const nearestToCenter = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let best = null;
      let bestDist = Infinity;
      for (const s of slides) {
        const x = s.offsetLeft + s.clientWidth / 2;
        const d = Math.abs(x - center);
        if (d < bestDist) { bestDist = d; best = s; }
      }
      return best;
    };

    const setCoverflow = (activeEl) => {
      const i = slides.indexOf(activeEl);
      slides.forEach((s, idx) => {
        const card = s.querySelector('.cr-reels__card');
        if (!card) return;
        if (idx === i) {
          card.style.setProperty('--crs', centerScale);
          card.style.setProperty('--cry', '0px');
        } else if (Math.abs(idx - i) === 1) {
          card.style.setProperty('--crs', Math.max(sideScale, centerScale - 0.08));
          card.style.setProperty('--cry', '8px');
        } else {
          card.style.setProperty('--crs', sideScale);
          card.style.setProperty('--cry', '14px');
        }
      });
    };

    const pauseAll = (exceptEl) => {
      slides.forEach(s => {
        const v = s.querySelector('video');
        if (!v) return;
        if (exceptEl && s === exceptEl) return;
        try { v.pause(); } catch(_) {}
        try { v.currentTime = 0; } catch(_) {}
      });
    };

    const playOnly = (slideEl) => {
      const v = slideEl ? slideEl.querySelector('video') : null;
      if (!v) return;
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };

    let activeSlide = null;
    let isJumping = false;
    let scrollEndTimer = null;

    const normalizeInfinite = () => {
      if (!n || !cloneCount) return;
      if (!stride) stride = calcStride();
      if (!stride) return;

      const leftBoundary  = slides[realStart]?.offsetLeft - (stride * 0.75);
      const rightBoundary = slides[realEnd]?.offsetLeft + (stride * 0.75);
      if (leftBoundary == null || rightBoundary == null) return;

      if (track.scrollLeft < leftBoundary) {
        isJumping = true;
        track.scrollLeft = track.scrollLeft + (n * stride);
        isJumping = false;
      } else if (track.scrollLeft > rightBoundary) {
        isJumping = true;
        track.scrollLeft = track.scrollLeft - (n * stride);
        isJumping = false;
      }
    };

    const applyActive = (el, playNow) => {
      if (!el) return;
      setCoverflow(el);
      if (!playNow) return;
      if (activeSlide === el) return;
      activeSlide = el;
      pauseAll(activeSlide);
      playOnly(activeSlide);
    };

    const snapTo = (el) => el && el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    const onScroll = () => {
      if (isJumping) return;
      normalizeInfinite();

      const candidate = nearestToCenter();
      if (candidate) setCoverflow(candidate);

      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        normalizeInfinite();
        const settled = nearestToCenter();
        applyActive(settled, true);
      }, 140);
    };

    track.addEventListener('scroll', onScroll, { passive: true });

    root.addEventListener('click', (e) => {
      const muteBtn = e.target.closest('.cr-reels__mute');
      if (muteBtn) {
        e.preventDefault();
        e.stopPropagation();

        const slide = muteBtn.closest('.cr-reels__slide');
        const v = slide ? slide.querySelector('video') : null;
        if (!v) return;

        v.muted = !v.muted;
        muteBtn.innerHTML = v.muted
          ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H3v6h3l5 4V5z"></path><path d="M22 9l-6 6"></path><path d="M16 9l6 6"></path></svg>'
          : '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H3v6h3l5 4V5z"></path><path d="M15 9a3 3 0 0 1 0 6"></path><path d="M19 7a6 6 0 0 1 0 10"></path></svg>';

        const p = v.play();
        if (p && p.catch) p.catch(() => {});
        return;
      }

      if (e.target.closest('.cr-reels__btn')) return;

      const slide = e.target.closest('.cr-reels__slide');
      if (slide) snapTo(slide);
    });

    const prevBtn = root.querySelector('.cr-reels__nav--prev');
    const nextBtn = root.querySelector('.cr-reels__nav--next');

    const scrollByOne = (dir) => {
      if (!stride) stride = calcStride();
      track.scrollBy({ left: dir * (stride || 260), behavior: 'smooth' });
    };

    prevBtn && prevBtn.addEventListener('click', () => scrollByOne(-1));
    nextBtn && nextBtn.addEventListener('click', () => scrollByOne( 1));

    const init = () => {
      stride = calcStride();
      // Start at the first real slide if we have clones
      if (cloneCount && slides[realStart]) {
        slides[realStart].scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      }
      setTimeout(() => {
        normalizeInfinite();
        const settled = nearestToCenter();
        applyActive(settled, true);
      }, 60);
    };

    init();

    window.addEventListener('resize', () => {
      stride = calcStride();
      normalizeInfinite();
      const settled = nearestToCenter();
      setCoverflow(settled);
    }, { passive: true });
  };

  const initAllCrReels = (scope = document) => {
    const roots = scope.querySelectorAll('[data-cr-reels]');
    if (!roots.length) return;

    if (supportsIO) {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          initCrReels(entry.target);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '200px 0px' });

      roots.forEach((el) => {
        if (el.dataset.crInit === '1') return;
        io.observe(el);
      });
    } else {
      roots.forEach(initCrReels);
    }
  };

  /* ===== boot ===== */
  const boot = (scope = document) => {
    initAllProductCarousels(scope);
    initAllCrReels(scope);
  };

  document.addEventListener('DOMContentLoaded', () => boot(document));
  document.addEventListener('shopify:section:load', (e) => boot(e.target || document));
})();
